import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Instagram } from 'lucide-react';
import Logo from '../Logo';
import { LEGAL_NAV } from '../../data/legal';

const PAGES = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/resources', label: 'Resources' },
  { to: '/contact', label: 'Contact' },
];

const TRADE_LINKS = [
  { to: '/websites-for-plumbers', label: 'Websites for Plumbers' },
  { to: '/websites-for-electricians', label: 'Websites for Electricians' },
  { to: '/websites-for-builders', label: 'Websites for Builders' },
  { to: '/websites-for-painters-decorators', label: 'Websites for Decorators' },
  { to: '/websites-for-roofers', label: 'Websites for Roofers' },
  { to: '/websites-for-landscapers', label: 'Websites for Landscapers' },
  { to: '/websites-for-carpenters', label: 'Websites for Carpenters' },
  { to: '/websites-for-plasterers', label: 'Websites for Plasterers' },
  { to: '/websites-for-handymen', label: 'Websites for Handymen' },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-charcoal-deep text-offwhite" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo dark />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-offwhite/65">
              Websites, Google Business Profiles and social media setup for UK tradespeople. You do the trade — we
              build the business.
            </p>
            <Link
              to="/contact"
              data-testid="footer-quote-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-lime px-5 py-2.5 font-heading text-sm font-bold text-charcoal transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-lime-hover"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
          <div className="lg:col-span-2">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-offwhite/45">Pages</h2>
            <ul className="mt-5 space-y-3">
              {PAGES.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    className="text-sm text-offwhite/75 transition-colors hover:text-lime"
                    data-testid={`footer-nav-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-offwhite/45">Legal</h2>
            <ul className="mt-5 space-y-3">
              {LEGAL_NAV.map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/${l.slug}`}
                    className="text-sm text-offwhite/75 transition-colors hover:text-lime"
                    data-testid={`footer-legal-${l.slug}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-offwhite/45">Contact</h2>
            <p className="mt-5 text-sm leading-relaxed text-offwhite/65">
              The quickest way to reach us is the free quote form — tell us about your business and we’ll come
              straight back to you.
            </p>
            <a
              href="mailto:info@buildmytrade.uk"
              data-testid="footer-email-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-offwhite transition-colors hover:text-lime"
            >
              <Mail className="h-4 w-4 text-lime" aria-hidden="true" />
              info@buildmytrade.uk
            </a>
            <a
              href="https://www.instagram.com/buildmytradeuk"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-offwhite transition-colors hover:text-lime"
            >
              <Instagram className="h-4 w-4 text-lime" aria-hidden="true" />
              @buildmytradeuk
            </a>
          </div>
        </div>
        <div className="mt-14 border-t border-charcoal-line pt-8">
          <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-offwhite/45">By trade</h2>
          <ul className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
            {TRADE_LINKS.map((t) => (
              <li key={t.to}>
                <Link
                  to={t.to}
                  className="text-sm text-offwhite/75 transition-colors hover:text-lime"
                  data-testid={`footer-nav-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {t.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-14 flex flex-col gap-3 border-t border-charcoal-line pt-8 text-xs text-offwhite/45 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">© {year} Build My Trade. All rights reserved.</p>
          <p>Digital services for tradespeople across the UK.</p>
        </div>
      </div>
    </footer>
  );
}
